import { PhoneIcon, Trash2Icon } from "lucide-react";
import { useId } from "react";
import { type Control, type FieldPath, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import { Input } from "../../../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { PHONE_EXTENSION_MAX_LENGTH, PHONE_TYPE_OPTIONS } from "../constants";
import { useFormReadOnly } from "../readOnlyContext";
import type { SignersInput } from "../Signers.schema";
import { PhoneNumberInput } from "./PhoneNumberInput";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PersonPath = "driver" | "secondSigner";

interface PhoneRowProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  index: number;
  /** Called when the user clicks remove. Undefined for the primary row (index 0). */
  onRemove?: () => void;
}

// ---------------------------------------------------------------------------
// PhoneRow
//
// A single phone entry: split 3-segment phone number + type select (Personal/Work)
// + optional extension (Work type only) + remove button for non-primary rows.
//
// Primary row (index 0): type locked to "Personal" (disabled select).
// Secondary rows (index > 0): full Personal/Work select + removable.
// Extension input shown only when type === "Work".
// ---------------------------------------------------------------------------
export function PhoneRow({ control, personPath, index, onRemove }: PhoneRowProperties) {
  const { t } = useTranslation();
  const uid = useId();
  const isPrimary = index === 0;
  // The call icon (<a>) and the Radix type Select both ignore a disabled
  // <fieldset>, so under the VLA lock we disable them from the shared context.
  const readOnly = useFormReadOnly();

  const numberPath = `${personPath}.customerInfo.phones.${index}.number` as FieldPath<SignersInput>;
  const typePath = `${personPath}.customerInfo.phones.${index}.type` as FieldPath<SignersInput>;
  const extensionPath = `${personPath}.customerInfo.phones.${index}.extension` as FieldPath<SignersInput>;

  const { field: numberField, fieldState: numberFieldState } = useController({ control, name: numberPath });
  const { field: typeField } = useController({ control, name: typePath });
  const { field: extensionField, fieldState: extensionFieldState } = useController({ control, name: extensionPath });

  const typeSelectId = `${uid}-phone-type`;
  const extensionInputId = `${uid}-phone-extension`;

  // Derive the visible row label — used for the <label> element and as the
  // ariaLabel base for the split phone input.
  let rowLabel: string;
  if (isPrimary) {
    rowLabel = t("review.customer.contact.primaryPhone");
  } else if (index === 1) {
    rowLabel = t("review.customer.contact.secondaryPhone");
  } else {
    rowLabel = t("review.customer.contact.phone");
  }

  // For index >= 2, multiple rows share the label "Phone". Append the position
  // (1-based) so each segment's aria-label remains unique (e.g. "Phone 2 area code").
  const ariaLabelBase = index >= 2 ? `${rowLabel} ${index}` : rowLabel;

  const typeValue = typeField.value == null ? "Personal" : String(typeField.value);
  const isWork = typeValue === "Work";

  // Canonical digits for the tel: link (the call icon dials the entered number).
  const numberDigits = (numberField.value == null ? "" : String(numberField.value)).replaceAll(/\D/g, "");

  function handleTypeChange(newType: string) {
    typeField.onChange(newType);
    // Clear extension when switching away from Work
    if (newType !== "Work") {
      extensionField.onChange("");
    }
  }

  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
      {/* Label — fixed-width column on sm+ so all rows align */}
      <label className="text-xs font-medium sm:w-32 sm:shrink-0">
        {rowLabel}
        {isPrimary && (
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {/* Inline controls + inline errors below them */}
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          {/* Phone number segment input */}
          <PhoneNumberInput
            idBase={`${uid}-phone-number`}
            value={numberField.value == null ? "" : String(numberField.value)}
            onChange={numberField.onChange}
            onBlur={numberField.onBlur}
            ariaLabel={ariaLabelBase}
            required={isPrimary}
            invalid={Boolean(numberFieldState.error)}
          />

          {/* Phone type select — aria-labelled only (no visible sub-label) */}
          <Select value={typeValue} onValueChange={handleTypeChange} disabled={isPrimary || readOnly}>
            <SelectTrigger
              id={typeSelectId}
              className="w-32"
              aria-label={t("review.customer.contact.phoneType")}
              disabled={isPrimary || readOnly}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PHONE_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Extension — only visible when type === "Work"; aria-labelled only */}
          {isWork && (
            <Input
              id={extensionInputId}
              type="text"
              inputMode="numeric"
              value={extensionField.value == null ? "" : String(extensionField.value)}
              onChange={(event) => {
                extensionField.onChange(event.target.value.replaceAll(/\D/g, "").slice(0, PHONE_EXTENSION_MAX_LENGTH));
              }}
              onBlur={extensionField.onBlur}
              ref={extensionField.ref}
              maxLength={PHONE_EXTENSION_MAX_LENGTH}
              placeholder={t("review.customer.contact.extension")}
              aria-label={t("review.customer.contact.extension")}
              aria-invalid={Boolean(extensionFieldState.error) || undefined}
              className="w-16"
            />
          )}

          {/* Call icon — dials the entered number via tel: when a number is
              present and the deal is editable. Under the VLA lock (read-only)
              the same slot shows a muted, inert icon (an <a> can't be disabled
              by the enclosing fieldset). When empty, a purely visual placeholder
              span of the same size reserves the layout slot without announcing a
              dead button to assistive technology. */}
          {numberDigits && !readOnly ? (
            <a
              href={`tel:${numberDigits}`}
              aria-label={t("review.customer.contact.call")}
              className="text-success-foreground border-success-foreground hover:bg-success flex size-6 shrink-0 items-center justify-center rounded-md border transition-colors"
            >
              <PhoneIcon className="size-3.5 fill-current" aria-hidden="true" />
            </a>
          ) : (
            <span
              aria-hidden="true"
              className={
                numberDigits
                  ? "text-muted-foreground border-muted flex size-6 shrink-0 items-center justify-center rounded-md border opacity-60"
                  : "border-success-foreground flex size-6 shrink-0 items-center justify-center rounded-md border"
              }
            >
              {numberDigits && <PhoneIcon className="size-3.5 fill-current" aria-hidden="true" />}
            </span>
          )}

          {/* Remove button — non-primary rows. The primary row reserves an
              equal-size empty slot so the delete column stays at a fixed
              horizontal position across all rows (and the Add link aligns to it). */}
          {!isPrimary && onRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onRemove}
              aria-label={t("review.customer.contact.removePhone", { position: index + 1 })}
              className="text-destructive hover:text-destructive shrink-0"
            >
              <Trash2Icon className="size-4" aria-hidden="true" />
            </Button>
          ) : (
            <span aria-hidden="true" className="size-8 shrink-0" />
          )}
        </div>

        {numberFieldState.error && (
          <p className="text-destructive text-2xs" role="alert">
            {numberFieldState.error.message}
          </p>
        )}
        {extensionFieldState.error && (
          <p className="text-destructive text-2xs" role="alert">
            {extensionFieldState.error.message}
          </p>
        )}
      </div>
    </div>
  );
}
